import { useState, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Skeleton,
  Switch,
} from '@ui';
import { cn } from '@ui/lib/utils';
import {
  Plus,
  LayoutTemplate,
  Edit,
  Trash2,
  Search,
  Copy,
  MoreVertical,
  Clock,
  Activity,
  Power,
  Loader2,
} from 'lucide-react';
import { useGroupStore } from '@/stores/groupStore';
import { useGetActivityTypeList } from '@hooks/generated/group';
import {
  useGetTemplateList1,
  useGetTemplate1,
  useCreateTemplate2,
  useUpdateTemplate2,
  useDeleteTemplate2,
  useToggleTemplate,
  useAddItem,
  useUpdateItem,
  useDeleteItem,
} from '@hooks/generated/activity-template';
import { useQueryClient } from '@tanstack/react-query';
import { toastMessage } from '@ui/components/Toast';
import ActivityTemplateModal from './ActivityTemplateModal';

// 활동 템플릿 항목 타입 (API 응답/요청 타입)
interface TemplateItem {
  id: number;
  activityTypeId: number;
  activityTypeName?: string;
  activityTypeColor?: string;
  startTime: string;
  endTime?: string | null;
  isInstantComplete: boolean;
  defaultContent?: string;
  isRequired: boolean;
  order: number;
  name?: string;
  type?: string;
}

// 활동 템플릿 타입 (API 응답 타입)
interface ActivityTemplate {
  id: number;
  name: string;
  description?: string;
  color?: string;
  type?: string;
  isActive: boolean;
  items: TemplateItem[];
  createdAt?: string;
  updatedAt?: string;
}

// 활동 타입 인터페이스 (API 응답 타입)
interface ActivityType {
  id: number;
  name: string;
  type?: string;
  description?: string;
  color?: string;
  isActive: boolean;
  price?: number;
  defaultAmount?: number;
}

// 미니 타임라인 컴포넌트
function MiniTimeline({ items, activityTypes }: { items: TemplateItem[]; activityTypes: ActivityType[] }) {
  if (!items || items.length === 0) {
    return <div className="text-xs text-neutral-400 dark:text-neutral-500">활동 없음</div>;
  }

  const sortedItems = [...items].sort((a, b) => {
    const aTime = a.startTime.split(':').map(Number);
    const bTime = b.startTime.split(':').map(Number);
    return aTime[0] * 60 + aTime[1] - (bTime[0] * 60 + bTime[1]);
  });

  return (
    <div className="flex items-center gap-1 overflow-hidden">
      {sortedItems.slice(0, 5).map((item, index) => {
        const activityType = activityTypes.find((t) => t.id === item.activityTypeId);
        const color = item.activityTypeColor || activityType?.color || '#6B7280';
        return (
          <div key={item.id || index} className="flex items-center">
            {index > 0 && (
              <div className="w-2 h-px bg-neutral-300 dark:bg-neutral-600 mx-0.5" />
            )}
            <div
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
              style={{
                backgroundColor: `${color}15`,
                color: color,
              }}
              title={`${item.startTime} ${item.activityTypeName || activityType?.name || '활동'}`}
            >
              {item.startTime.slice(0, 5)}
            </div>
          </div>
        );
      })}
      {sortedItems.length > 5 && (
        <span className="text-[10px] text-neutral-400 dark:text-neutral-500 ml-1">
          +{sortedItems.length - 5}
        </span>
      )}
    </div>
  );
}

// 템플릿 카드 컴포넌트
function TemplateCard({
  template,
  activityTypes,
  onEdit,
  onDelete,
  onDuplicate,
  onToggle,
  isToggling,
}: {
  template: ActivityTemplate;
  activityTypes: ActivityType[];
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onToggle: () => void;
  isToggling: boolean;
}) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div
      className={cn(
        'group relative rounded-xl border bg-white dark:bg-neutral-900 transition-all duration-200 hover:shadow-md',
        !template.isActive && 'opacity-60 grayscale-[30%]'
      )}
    >
      <div className="p-4">
        {/* 헤더 */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <LayoutTemplate className="h-4 w-4 flex-shrink-0 text-neutral-500 dark:text-neutral-400" />
            <h4 className="font-semibold text-neutral-800 dark:text-neutral-100 truncate">
              {template.name}
            </h4>
          </div>

          {/* 메뉴 버튼 */}
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-8 z-20 w-36 bg-white dark:bg-neutral-900 rounded-lg shadow-lg border py-1">
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    onClick={() => {
                      setShowMenu(false);
                      onEdit();
                    }}
                  >
                    <Edit className="h-4 w-4" />
                    수정
                  </button>
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    onClick={() => {
                      setShowMenu(false);
                      onDuplicate();
                    }}
                  >
                    <Copy className="h-4 w-4" />
                    복제
                  </button>
                  <button
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={() => {
                      setShowMenu(false);
                      onDelete();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                    삭제
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 활동 정보 */}
        <div className="flex items-center gap-4 text-sm text-neutral-600 dark:text-neutral-300 mb-3">
          <div className="flex items-center gap-1">
            <Activity className="h-3.5 w-3.5" />
            <span>{template.items?.length || 0}개 활동</span>
          </div>
          {template.items && template.items.length > 0 && (
            <div className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {template.items[0]?.startTime?.slice(0, 5)} ~{' '}
                {template.items[template.items.length - 1]?.endTime?.slice(0, 5) ||
                  template.items[template.items.length - 1]?.startTime?.slice(0, 5)}
              </span>
            </div>
          )}
        </div>

        {/* 미니 타임라인 */}
        <MiniTimeline items={template.items || []} activityTypes={activityTypes} />


        {/* 설명 */}
        <p className="text-sm text-neutral-500 dark:text-neutral-400 my-4 line-clamp-2">
          {template.description ? template.description : "-"}
        </p>


        {/* 활성/비활성 토글 */}
        <div className="mt-3 pt-3 border-t flex items-center justify-between">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {template.isActive ? '활성' : '비활성'}
          </span>
          <div className="flex items-center gap-2">
            {isToggling && <Loader2 className="h-3 w-3 animate-spin text-neutral-400 dark:text-neutral-500" />}
            <Switch
              checked={template.isActive}
              onCheckedChange={onToggle}
              disabled={isToggling}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ActivityTemplateTab() {
  const selectedGroup = useGroupStore((state) => state.selectedGroup);
  const groupId = selectedGroup?.id;
  const queryClient = useQueryClient();

  // 검색 상태
  const [searchQuery, setSearchQuery] = useState('');

  // 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);

  // 삭제 확인 상태
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  // 템플릿 목록 조회
  const { data: templatesData, isLoading: isLoadingTemplates } = useGetTemplateList1({
    request: {
      groupId: groupId || 0,
      includeInactive: true,
    },
    options: {
      enabled: !!groupId,
    },
  });

  // 템플릿 상세 조회 (수정 모드)
  const { data: templateDetailData, isLoading: isLoadingDetail } = useGetTemplate1({
    request: {
      groupId: groupId || 0,
      templateId: editingTemplateId || 0,
    },
    options: {
      enabled: !!groupId && !!editingTemplateId,
    },
  });

  // 수정 모드에서 사용할 템플릿 데이터
  const editingTemplate: ActivityTemplate | null = useMemo(() => {
    if (!editingTemplateId || !templateDetailData) return null;
    const data = templateDetailData as any;
    return {
      id: data.id,
      name: data.name,
      description: data.description,
      price: data.defaultAmount,
      color: data.color,
      isActive: data.isActive !== false,
      items: data.items || [],
    };
  }, [editingTemplateId, templateDetailData]);

  // 활동 타입 목록 조회
  const { data: activityTypesData } = useGetActivityTypeList({
    request: { groupId: groupId || 0, includeInactive: false },
    options: { enabled: !!groupId },
  });

  // 템플릿 생성 mutation (handleCreateTemplate에서 직접 처리하므로 onSuccess에서 closeModal 제거)
  const createMutation = useCreateTemplate2({
    options: {
      onError: (error: any) => {
        console.error('Create template error:', error);
        toastMessage({ message: '템플릿 생성에 실패했습니다.', type: 'error' });
      },
    },
  });

  // 템플릿 수정 mutation (handleUpdateTemplate에서 직접 처리하므로 onSuccess에서 closeModal 제거)
  const updateMutation = useUpdateTemplate2({
    options: {
      onError: (error: any) => {
        console.error('Update template error:', error);
        toastMessage({ message: '템플릿 수정에 실패했습니다.', type: 'error' });
      },
    },
  });

  // 템플릿 삭제 mutation
  const deleteMutation = useDeleteTemplate2({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] });
        toastMessage({ message: '템플릿이 삭제되었습니다.', type: 'success' });
        setDeleteConfirmId(null);
      },
      onError: (error: any) => {
        console.error('Delete template error:', error);
        toastMessage({ message: '템플릿 삭제에 실패했습니다.', type: 'error' });
      },
    },
  });

  // 템플릿 토글 mutation
  const toggleMutation = useToggleTemplate({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] });
        toastMessage({ message: '템플릿 상태가 변경되었습니다.', type: 'success' });
        setTogglingId(null);
      },
      onError: (error) => {
        console.error('Toggle template error:', error);
        toastMessage({ message: '상태 변경에 실패했습니다.', type: 'error' });
        setTogglingId(null);
      },
    },
  });

  // 활동 아이템 추가 mutation
  const addItemMutation = useAddItem({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getTemplate'] });
        queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] });
        toastMessage({ message: '활동이 추가되었습니다.', type: 'success' });
      },
      onError: (error) => {
        console.error('Add item error:', error);
        toastMessage({ message: '활동 추가에 실패했습니다.', type: 'error' });
      },
    },
  });

  // 활동 아이템 수정 mutation
  const updateItemMutation = useUpdateItem({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getTemplate'] });
        queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] });
        toastMessage({ message: '활동이 수정되었습니다.', type: 'success' });
      },
      onError: (error) => {
        console.error('Update item error:', error);
        toastMessage({ message: '활동 수정에 실패했습니다.', type: 'error' });
      },
    },
  });

  // 활동 아이템 삭제 mutation
  const deleteItemMutation = useDeleteItem({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getTemplate'] });
        queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] });
        toastMessage({ message: '활동이 삭제되었습니다.', type: 'success' });
      },
      onError: (error) => {
        console.error('Delete item error:', error);
        toastMessage({ message: '활동 삭제에 실패했습니다.', type: 'error' });
      },
    },
  });

  // 템플릿 데이터 파싱
  const templates: ActivityTemplate[] = useMemo(() => {
    if (!templatesData) return [];
    if (Array.isArray(templatesData)) {
      return templatesData.map((t: any) => ({
        id: t.id,
        name: t.name,
        description: t.description,
        color: t.color,
        isActive: t.isActive !== false,
        items: t.items || [],
      }));
    }
    return [];
  }, [templatesData]);

  const activityTypes: ActivityType[] = useMemo(() => {
    if (!activityTypesData || !Array.isArray(activityTypesData)) {
      // 기본 활동 타입 (API 응답이 없을 때)
      return [
        { id: 1, name: '급식', color: '#10B981', isActive: true },
        { id: 2, name: '산책', color: '#3B82F6', isActive: true },
        { id: 3, name: '휴식', color: '#8B5CF6', isActive: true },
        { id: 4, name: '투약', color: '#EF4444', isActive: true },
        { id: 5, name: '미용', color: '#F59E0B', isActive: true },
        { id: 6, name: '배변', color: '#6B7280', isActive: true },
      ];
    }
    return activityTypesData as ActivityType[];
  }, [activityTypesData]);

  // 필터링된 템플릿
  const filteredTemplates = useMemo(() => {
    if (!searchQuery) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(
      (template) =>
        template.name.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query)
    );
  }, [templates, searchQuery]);

  // 모달 핸들러
  const openCreateModal = useCallback(() => {
    setEditingTemplateId(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((templateId: number) => {
    setEditingTemplateId(templateId);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingTemplateId(null);
  }, []);

  // Step 1에서 템플릿 생성 핸들러
  const handleCreateTemplate = useCallback(
    async (data: { name: string; description?: string; price?: number }): Promise<number | null> => {
      if (!groupId) return null;

      try {
        // API 스펙에 맞춰 타입 단언 사용
        const result = await createMutation.mutateAsync({
          groupId,
          activityTemplateCreateRequest: {
            name: data.name,
            type: 'HOURLY' as const,
            description: data.description,
            defaultAmount: data.price,
          } as Parameters<typeof createMutation.mutateAsync>[0]['activityTemplateCreateRequest'],
        });

        // 생성된 템플릿 ID 반환 (AxiosResponse<ApiResponseDetailResponse> 구조)
        const newTemplateId = (result?.data?.data as any)?.id || (result?.data as any)?.id || (result as any)?.id;
        if (newTemplateId) {
          setEditingTemplateId(newTemplateId);
          queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] });
          toastMessage({ message: '템플릿이 생성되었습니다. 활동을 등록해주세요.', type: 'success' });
          return newTemplateId;
        }
        console.error('Template ID not found in response:', result);
        toastMessage({ message: '템플릿 ID를 찾을 수 없습니다.', type: 'error' });
        return null;
      } catch (error) {
        console.error('Create template error:', error);
        toastMessage({ message: '템플릿 생성에 실패했습니다.', type: 'error' });
        return null;
      }
    },
    [groupId, createMutation, queryClient]
  );

  // Step 1에서 템플릿 수정 핸들러 (기본 정보만 업데이트)
  const handleUpdateTemplate = useCallback(
    async (templateId: number, data: { name: string; description?: string; price?: number }): Promise<boolean> => {
      if (!groupId) return false;

      try {
        await updateMutation.mutateAsync({
          groupId,
          templateId,
          activityTemplateUpdateRequest: {
            name: data.name,
            description: data.description,
            defaultAmount: data.price,
          } as Parameters<typeof updateMutation.mutateAsync>[0]['activityTemplateUpdateRequest'],
        });

        queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] });
        queryClient.invalidateQueries({ queryKey: ['getTemplate1', groupId, templateId] });
        toastMessage({ message: '템플릿 정보가 수정되었습니다.', type: 'success' });
        return true;
      } catch (error) {
        console.error('Update template error:', error);
        toastMessage({ message: '템플릿 수정에 실패했습니다.', type: 'error' });
        return false;
      }
    },
    [groupId, updateMutation, queryClient]
  );

  // 저장 핸들러 (Step 2에서 활동 저장) - Promise 반환
  const handleSave = useCallback(
    async (data: {
      name: string;
      description?: string;
      color?: string;
      items: Array<{
        activityTypeId: number;
        startTime: string;
        endTime?: string | null;
        isInstantComplete: boolean;
        defaultContent?: string;
        isRequired: boolean;
        order: number;
      }>;
    }): Promise<boolean> => {
      if (!groupId) return false;

      // CreateRequest 형식으로 변환
      const requestData = {
        name: data.name,
        description: data.description,
        color: data.color,
        type: 'HOURLY' as const,
        items: data.items,
      };

      try {
        if (editingTemplateId) {
          // 수정 (기존 템플릿 업데이트) - API 스펙에 맞춰 타입 단언 사용
          await updateMutation.mutateAsync({
            groupId,
            templateId: editingTemplateId,
            activityTemplateUpdateRequest: {
              name: requestData.name,
              description: requestData.description,
            } as Parameters<typeof updateMutation.mutateAsync>[0]['activityTemplateUpdateRequest'],
          });
        } else {
          // 생성 (onCreateTemplate 없이 한 번에 생성하는 경우) - API 스펙에 맞춰 타입 단언 사용
          await createMutation.mutateAsync({
            groupId,
            activityTemplateCreateRequest: {
              name: requestData.name,
              type: requestData.type as 'HOURLY',
              description: requestData.description,
            } as Parameters<typeof createMutation.mutateAsync>[0]['activityTemplateCreateRequest'],
          });
        }
        return true;
      } catch (error) {
        // 에러는 mutation의 onError에서 처리됨
        throw error;
      }
    },
    [groupId, editingTemplateId, createMutation, updateMutation]
  );

  // 삭제 핸들러
  const handleDelete = useCallback((templateId: number) => {
    if (!groupId) return;
    deleteMutation.mutate({
      groupId,
      templateId,
    });
  }, [groupId, deleteMutation]);

  // 복제 핸들러
  const handleDuplicate = useCallback((template: ActivityTemplate) => {
    if (!groupId) return;

    // API 스펙에 맞춰 타입 단언 사용
    createMutation.mutate({
      groupId,
      activityTemplateCreateRequest: {
        name: `${template.name} (복사본)`,
        type: 'HOURLY' as const,
        description: template.description,
      } as Parameters<typeof createMutation.mutate>[0]['activityTemplateCreateRequest'],
    });
  }, [groupId, createMutation]);

  // 토글 핸들러
  const handleToggle = useCallback((templateId: number) => {
    if (!groupId) return;
    setTogglingId(templateId);
    toggleMutation.mutate({
      groupId,
      templateId,
    });
  }, [groupId, toggleMutation]);

  // 활동 아이템 추가 핸들러
  const handleAddItem = useCallback(
    async (templateId: number, item: {
      activityTypeId: number;
      startTime: string;
      endTime?: string | null;
      isInstantComplete: boolean;
      defaultContent?: string;
      isRequired: boolean;
      order: number;
    }): Promise<number | null> => {
      if (!groupId) return null;
      try {
        const activityType = activityTypes.find((t) => t.id === item.activityTypeId);
        // API 스펙에 맞춰 타입 단언 사용
        const result = await addItemMutation.mutateAsync({
          groupId,
          templateId,
          activityTemplateItemCreateRequest: {
            name: activityType?.name || '활동',
            type: 'HOURLY' as const,
            activityTypeId: item.activityTypeId,
            startTime: item.startTime,
            endTime: item.endTime || undefined,
            isInstantComplete: item.isInstantComplete,
            defaultContent: item.defaultContent,
            isRequired: item.isRequired,
            order: item.order,
          } as Parameters<typeof addItemMutation.mutateAsync>[0]['activityTemplateItemCreateRequest'],
        });
        const responseData = result?.data?.data as { id?: number } | undefined;
        return responseData?.id || null;
      } catch (error) {
        console.error('Add item error:', error);
        return null;
      }
    },
    [groupId, addItemMutation, activityTypes]
  );

  // 활동 아이템 수정 핸들러
  const handleUpdateItem = useCallback(
    async (templateId: number, itemId: number, item: {
      activityTypeId: number;
      startTime: string;
      endTime?: string | null;
      isInstantComplete: boolean;
      defaultContent?: string;
      isRequired: boolean;
      order: number;
    }): Promise<boolean> => {
      if (!groupId) return false;
      try {
        const activityType = activityTypes.find((t) => t.id === item.activityTypeId);
        // API 스펙에 맞춰 타입 단언 사용
        await updateItemMutation.mutateAsync({
          groupId,
          templateId,
          itemId,
          activityTemplateItemUpdateRequest: {
            name: activityType?.name || '활동',
            activityTypeId: item.activityTypeId,
            startTime: item.startTime,
            endTime: item.endTime || undefined,
            isInstantComplete: item.isInstantComplete,
            defaultContent: item.defaultContent,
            isRequired: item.isRequired,
            order: item.order,
          } as Parameters<typeof updateItemMutation.mutateAsync>[0]['activityTemplateItemUpdateRequest'],
        });
        return true;
      } catch (error) {
        console.error('Update item error:', error);
        return false;
      }
    },
    [groupId, updateItemMutation, activityTypes]
  );

  // 활동 아이템 삭제 핸들러
  const handleDeleteItem = useCallback(
    async (templateId: number, itemId: number): Promise<boolean> => {
      if (!groupId) return false;
      try {
        await deleteItemMutation.mutateAsync({
          groupId,
          templateId,
          itemId,
        });
        return true;
      } catch (error) {
        console.error('Delete item error:', error);
        return false;
      }
    },
    [groupId, deleteItemMutation]
  );

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
            활동 템플릿 관리
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            일정에 적용할 수 있는 활동 템플릿을 관리합니다.
          </p>
        </div>
        <Button onClick={openCreateModal} disabled={createMutation.isPending}>
          {createMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          새 템플릿
        </Button>
      </div>

      {/* 검색 */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 dark:text-neutral-500" />
        <Input
          placeholder="템플릿 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* 로딩 상태 */}
      {isLoadingTemplates ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <div className="h-1 w-full rounded-t-xl bg-neutral-200 dark:bg-neutral-700" />
              <CardContent className="p-4 space-y-3">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-24" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-6 w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <LayoutTemplate className="h-12 w-12 text-neutral-300 mb-4" />
            <h3 className="text-lg font-medium text-neutral-600 dark:text-neutral-300 mb-2">
              {searchQuery ? '검색 결과가 없습니다' : '템플릿이 없습니다'}
            </h3>
            <p className="text-sm text-neutral-400 dark:text-neutral-500 mb-4">
              {searchQuery
                ? '다른 검색어를 시도해보세요.'
                : '새 템플릿을 만들어 일정에 활동을 빠르게 추가하세요.'}
            </p>
            {!searchQuery && (
              <Button onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-2" />
                새 템플릿 만들기
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              activityTypes={activityTypes}
              onEdit={() => openEditModal(template.id)}
              onDelete={() => setDeleteConfirmId(template.id)}
              onDuplicate={() => handleDuplicate(template)}
              onToggle={() => handleToggle(template.id)}
              isToggling={togglingId === template.id}
            />
          ))}
        </div>
      )}

      {/* 템플릿 모달 */}
      <ActivityTemplateModal
        open={isModalOpen}
        onClose={closeModal}
        template={editingTemplate}
        templateId={editingTemplateId}
        activityTypes={activityTypes}
        onSave={handleSave}
        onCreateTemplate={handleCreateTemplate}
        onUpdateTemplate={handleUpdateTemplate}
        onAddItem={handleAddItem}
        onUpdateItem={handleUpdateItem}
        onDeleteItem={handleDeleteItem}
        isCreating={createMutation.isPending}
        isItemMutating={addItemMutation.isPending || updateItemMutation.isPending || deleteItemMutation.isPending}
        onRefetch={() => queryClient.invalidateQueries({ queryKey: ['getTemplateList1'] })}
      />

      {/* 삭제 확인 다이얼로그 */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 mb-2">
                템플릿 삭제
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
                이 템플릿을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setDeleteConfirmId(null)}
                  disabled={deleteMutation.isPending}
                >
                  취소
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDelete(deleteConfirmId)}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : null}
                  삭제
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
